import React, { useEffect, useRef, useState } from "react";
import { Alert } from "antd";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { mapSegments, stopPoint, validMapPoint, mapDayLabel } from "../utils/mapTours";

const time = value => !value ? "–" : Array.isArray(value) ? value.slice(0, 2).map(n => String(n).padStart(2, "0")).join(":") : String(value).slice(0, 5);
const line = (parent, text, bold = false) => {
  const node = document.createElement(bold ? "strong" : "div");
  node.textContent = text;
  parent.appendChild(node);
};
const heading = tour => `${tour.busName || `Bus ${tour.busId}`} · ${tour.richtung === "HIN" ? "Hinfahrt" : "Rückfahrt"} · ${mapDayLabel(tour.days)}`;

export default function ToursMap({ tours, focus }) {
  const container = useRef(null);
  const mapRef = useRef(null);
  const boundsRef = useRef(new Map());
  const layersRef = useRef(null);
  const [tileError, setTileError] = useState(false);
  useEffect(() => {
    const map = L.map(container.current, { center: [48.5, 9], zoom: 8, preferCanvas: true });
    mapRef.current = map;
    const tiles = L.tileLayer(process.env.REACT_APP_MAP_TILE_URL || "https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: process.env.REACT_APP_MAP_TILE_ATTRIBUTION || '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors',
    }).addTo(map);
    tiles.on("tileerror", () => setTileError(true));
    tiles.on("tileload", () => setTileError(false));
    L.control.scale({ imperial: false }).addTo(map);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(() => map.invalidateSize()) : null;
    observer?.observe(container.current);
    return () => { observer?.disconnect(); map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (layersRef.current) map.removeLayer(layersRef.current);
    const layer = L.featureGroup().addTo(map);
    layersRef.current = layer;
    const bounds = new Map();
    for (const tour of tours) {
      const points = [];
      for (const segment of mapSegments(tour)) {
        const path = L.polyline(segment.coordinates, { color: tour.color, weight: 4, opacity: 0.8,
          dashArray: segment.road ? undefined : "7 7" }).addTo(layer);
        for (const point of segment.coordinates) points.push(point);
        const popup = document.createElement("div");
        line(popup, heading(tour), true);
        line(popup, segment.road ? "Gespeicherter Straßenverlauf" : "Verbindung der Haltestellen – kein berechneter Straßenverlauf");
        path.bindPopup(popup);
      }
      const locations = new Map();
      (tour.stops || []).forEach((stop, index) => {
        const point = stopPoint(stop);
        if (!validMapPoint(point)) return;
        points.push(point);
        const key = point.join(":");
        if (!locations.has(key)) locations.set(key, { point, stops: [] });
        locations.get(key).stops.push({ ...stop, number: index + 1 });
      });
      for (const location of locations.values()) {
        const badge = document.createElement("span");
        location.stops.forEach((stop, index) => {
          const number = document.createElement("span");
          number.textContent = `${stop.number}${index < location.stops.length - 1 ? "," : ""}`;
          number.style.whiteSpace = "nowrap";
          badge.appendChild(number);
        });
        Object.assign(badge.style, { display: "inline-flex", flexWrap: "wrap", gap: "2px 3px", alignItems: "center", justifyContent: "center",
          width: "max-content", maxWidth: "180px", minWidth: "28px", minHeight: "28px",
          padding: "4px 6px", boxSizing: "border-box", borderRadius: "16px", background: tour.color,
          color: "#fff", fontWeight: "bold", fontSize: "12px", fontFamily: "Arial, sans-serif", lineHeight: "16px",
          border: "2px solid #fff", boxShadow: "0 1px 5px #0006" });
        // Measure the rendered content so Leaflet's hit area and anchor grow with it.
        badge.style.position = "absolute";
        badge.style.visibility = "hidden";
        map.getContainer().appendChild(badge);
        const size = badge.getBoundingClientRect();
        const width = Math.ceil(size.width), height = Math.ceil(size.height);
        badge.remove();
        badge.style.position = "";
        badge.style.visibility = "";
        const marker = L.marker(location.point, { icon: L.divIcon({ html: badge, className: "routing-stop-marker", iconSize: [width, height], iconAnchor: [width / 2, height / 2] }),
          title: `${heading(tour)} · Halt ${location.stops.map(stop => stop.number).join(", ")}` }).addTo(layer);
        const popup = document.createElement("div");
        line(popup, heading(tour), true);
        for (const stop of location.stops) {
          const section = document.createElement("div"); section.style.marginTop = "10px";
          line(section, `${stop.number}. ${stop.type === "DEPOT" ? "Depot" : stop.type === "PICKUP" ? "Abholung" : "Ausstieg"} · ${time(stop.scheduledTime)} Uhr`, true);
          if (stop.passengerName) line(section, stop.passengerName);
          line(section, stop.address || stop.location?.name || "–");
          if (stop.timeWindowViolation) line(section, stop.timeWindowViolation);
          popup.appendChild(section);
        }
        marker.bindPopup(popup, { maxWidth: 360 });
      }
      if (points.length) bounds.set(tour.key, L.latLngBounds(points));
    }
    boundsRef.current = bounds;
    const all = L.latLngBounds([]); for (const bound of bounds.values()) all.extend(bound);
    if (all.isValid()) map.fitBounds(all, { padding: [35, 35], maxZoom: 15 });
  }, [tours]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focus) return;
    const bounds = focus.key ? boundsRef.current.get(focus.key) : L.latLngBounds([]);
    if (!focus.key) for (const bound of boundsRef.current.values()) bounds.extend(bound);
    if (bounds?.isValid()) map.fitBounds(bounds, { padding: [45, 45], maxZoom: 16 });
  }, [focus]);
  return <>
    {tileError && <Alert type="warning" showIcon message="Der Kartenhintergrund konnte nicht vollständig geladen werden. Touren und Haltestellen bleiben sichtbar." style={{ marginBottom: 12 }} />}
    <div ref={container} role="region" aria-label="Karte der gespeicherten Touren" style={{ height: "65vh", minHeight: 420, maxHeight: 850, width: "100%", borderRadius: 10, zIndex: 0 }} />
  </>;
}
